import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AntibodyChain, AntibodyFormat, AntibodyVersion} from 'antibodies/AntibodyTypedefs';
import {chainColor, formatToSortedDistinctChains} from 'antibodies/AntibodyUtils';
import AntibodyComponentPopover from 'antibodies/visualization/AntibodyComponentPopover';
import InteractiveComponentWrapper, {
  AntibodyInteractivityProps,
} from 'antibodies/visualization/InteractiveComponentWrapper';
import {assert} from 'Asserts';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';

interface ChainLegendItemProps {
  chainRef: number;
  chainIndex: number;
  label: string;
  // TODO (BNCH-133226): Once we have entities for all chains, this should be required.
  entity: AntibodyChain['entity'] | null;
  interactivity?: AntibodyInteractivityProps;
}

export function ChainLegendItem({
  chainRef,
  chainIndex,
  label,
  entity,
  interactivity,
}: ChainLegendItemProps): JSX.Element {
  const inaccessible = entity != null && isInaccessible(entity);
  const proteinId = entity != null && !inaccessible ? entity.id : null;

  const legendItem = (
    <div className={css(styles.legendItem)}>
      <svg width={16} height={16}>
        <circle
          cx={8}
          cy={8}
          fill={chainColor(chainIndex)}
          r={7}
          {...getTestProps({component: 'AntibodyChainLegend', element: 'circle', key: chainRef.toString()})}
        />
      </svg>
      <span className={css(styles.legendItemText)}>{label}</span>
    </div>
  );
  return entity != null ? (
    <AntibodyComponentPopover proteinId={proteinId} inaccessible={inaccessible}>
      <InteractiveComponentWrapper
        componentRef={chainRef}
        interactivity={interactivity}
        interactiveElement="div"
      >
        {legendItem}
      </InteractiveComponentWrapper>
    </AntibodyComponentPopover>
  ) : (
    legendItem
  );
}

export interface AntibodyChainLegendProps {
  antibodyFormat: AntibodyFormat;
  antibodyVersion?: AntibodyVersion;
  interactivity?: AntibodyInteractivityProps;
}

export default function AntibodyChainLegend({
  antibodyFormat,
  antibodyVersion,
  interactivity,
}: AntibodyChainLegendProps): JSX.Element {
  const distinctChains = formatToSortedDistinctChains(antibodyFormat);
  return (
    <div className={css(styles.legend)}>
      {distinctChains.map((chain, chainIndex) => {
        let matchingChains: Array<AntibodyChain> = [];
        if (antibodyVersion != null) {
          matchingChains = antibodyVersion.chains.filter((c) => c.label === chain.label);
          assert(matchingChains.length >= 1, 'Expected at least one format chain for a distinct chain');
        }
        const matchingEntities = new Set(matchingChains.map((c) => c.entity));
        // TODO (BNCH-133226): Once we have entities for all chains, this should assert exactly one if the
        // antibody version is provided.
        assert(matchingEntities.size <= 1, 'Expected at most one entity for a distinct chain');
        return (
          <ChainLegendItem
            key={chainIndex}
            // We select the first ref here, since antibody map interactivity currently tracks a single ref
            // (ref ordering is deterministic).
            // Ideally, we would track interactivity at the distinct component level (or track multiple refs,
            // for each duplicate component).
            chainRef={chain.refs[0]}
            chainIndex={chainIndex}
            label={chain.label}
            entity={matchingEntities.size === 1 ? Array.from(matchingEntities)[0] : null}
            interactivity={interactivity}
          />
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  legend: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    flexWrap: 'wrap',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    padding: '8px',
    justifyContent: 'space-evenly',
    marginTop: '16px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  legendItemText: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    letterSpacing: '0',
  },
});
